import { Layout } from '@/components/layouts/layout';
import { Button } from '@/components/ui/button';
import { useInventoryAll } from '@/hooks/use-inventory';
import {
    useDismissNotification,
    useMarkAllNotificationsRead,
    useMarkNotificationRead,
    useNotifications,
} from '@/hooks/use-notifications';
import type { NotificationRecord } from '@/lib/notifications-api';
import { syncInventoryNotifications } from '@/lib/notifications-api';
import { cn } from '@/lib/utils';
import { Link, createFileRoute } from '@tanstack/react-router';
import {
    AlertCircle,
    Bell,
    Package,
    RefreshCw,
    TrendingDown,
    X,
} from 'lucide-react';
import { useEffect, useRef } from 'react';

export const Route = createFileRoute('/notifications')({
    component: Notifications,
});

const TYPE_CONFIG = {
    price_drop_suggestion: {
        Icon: TrendingDown,
        iconClass: 'text-amber-500',
        bgClass: 'bg-amber-500/10',
    },
    unlisted_60_days: {
        Icon: Package,
        iconClass: 'text-orange-500',
        bgClass: 'bg-orange-500/10',
    },
    renewal_upcoming: {
        Icon: RefreshCw,
        iconClass: 'text-blue-500',
        bgClass: 'bg-blue-500/10',
    },
    payment_failure: {
        Icon: AlertCircle,
        iconClass: 'text-red-500',
        bgClass: 'bg-red-500/10',
    },
} as const;

const INTENT_PX = 8;
const DISMISS_PX = 80;

function relativeTime(iso: string) {
    const days = Math.floor(
        (Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24),
    );
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 30) return `${days}d ago`;
    return new Date(iso).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
    });
}

function SwipeableNotification({
    notification,
    onDismiss,
    onMarkRead,
}: {
    notification: NotificationRecord;
    onDismiss: () => void;
    onMarkRead: () => void;
}) {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const cardRef = useRef<HTMLDivElement>(null);
    const startX = useRef(0);
    const lastX = useRef(0);
    const isActive = useRef(false);
    const hasIntent = useRef(false);

    const triggerDismiss = () => {
        const card = cardRef.current;
        const wrapper = wrapperRef.current;
        if (!card || !wrapper) {
            onDismiss();
            return;
        }
        // Step 1: slide card off-screen
        card.style.transition = 'transform 0.18s ease-out, opacity 0.18s ease-out';
        card.style.transform = 'translateX(-110%)';
        card.style.opacity = '0';

        // Step 2: collapse wrapper height to close the gap
        setTimeout(() => {
            const h = wrapper.clientHeight;
            wrapper.style.overflow = 'hidden';
            wrapper.style.height = `${h}px`;
            // Force reflow so the browser registers the fixed height
            void wrapper.offsetHeight;
            wrapper.style.transition =
                'height 0.22s ease, margin-bottom 0.22s ease, opacity 0.15s ease';
            wrapper.style.height = '0';
            wrapper.style.marginBottom = '0';
            wrapper.style.opacity = '0';
        }, 160);

        // Step 3: call API after full animation
        setTimeout(onDismiss, 390);
    };

    const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        startX.current = e.clientX;
        lastX.current = e.clientX;
        isActive.current = true;
        hasIntent.current = false;
        if (cardRef.current) cardRef.current.style.transition = 'none';
    };

    const onPointerMove = (e: React.PointerEvent) => {
        if (!isActive.current) return;
        const dx = e.clientX - startX.current;
        if (!hasIntent.current && Math.abs(dx) < INTENT_PX) return;
        hasIntent.current = true;
        lastX.current = e.clientX;
        const clamped = Math.min(0, dx); // left-only
        if (cardRef.current) {
            cardRef.current.style.transform = `translateX(${clamped}px)`;
            cardRef.current.style.opacity = `${Math.max(0.35, 1 - Math.abs(clamped) / 180)}`;
        }
    };

    const onPointerUp = () => {
        if (!isActive.current) return;
        isActive.current = false;
        const dx = lastX.current - startX.current;

        if (hasIntent.current && dx < -DISMISS_PX) {
            triggerDismiss();
        } else if (hasIntent.current && cardRef.current) {
            cardRef.current.style.transition =
                'transform 0.25s cubic-bezier(0.2,0.8,0.2,1), opacity 0.25s';
            cardRef.current.style.transform = 'translateX(0)';
            cardRef.current.style.opacity = '1';
        }

        hasIntent.current = false;
    };

    const isUnread = !notification.read_at;
    const config = TYPE_CONFIG[notification.type as keyof typeof TYPE_CONFIG];
    const Icon = config?.Icon ?? Bell;
    const itemId = notification.data.item_id as string | undefined;

    return (
        <div ref={wrapperRef}>
            <div
                ref={cardRef}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                style={{ touchAction: 'pan-y' }}
                className={cn(
                    'relative flex gap-3 rounded-lg border p-4 select-none',
                    isUnread
                        ? 'bg-card border-primary/30'
                        : 'bg-card/50 border-border',
                )}
            >
                {isUnread && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 rounded-r-full bg-primary" />
                )}

                <div
                    className={cn(
                        'shrink-0 size-9 rounded-full flex items-center justify-center',
                        config?.bgClass ?? 'bg-muted',
                    )}
                >
                    <Icon
                        className={cn(
                            'size-4',
                            config?.iconClass ?? 'text-muted-foreground',
                        )}
                    />
                </div>

                <div
                    className="flex-1 min-w-0 cursor-pointer"
                    onClick={() => {
                        if (isUnread) onMarkRead();
                    }}
                >
                    <div className="flex items-start justify-between gap-2">
                        <p
                            className={cn(
                                'text-sm leading-snug',
                                isUnread
                                    ? 'font-semibold'
                                    : 'font-medium text-muted-foreground',
                            )}
                        >
                            {notification.title}
                        </p>
                        <span className="shrink-0 text-xs text-muted-foreground whitespace-nowrap">
                            {relativeTime(notification.created_at)}
                        </span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-0.5 leading-snug">
                        {notification.body}
                    </p>
                    {itemId && (
                        <Link
                            to="/inventory/$itemId"
                            params={{ itemId }}
                            className="inline-block mt-2 text-xs text-primary hover:underline"
                            onClick={(e) => e.stopPropagation()}
                        >
                            View item →
                        </Link>
                    )}
                </div>

                <Button
                    variant="ghost"
                    size="icon"
                    className="shrink-0 size-7 self-start text-muted-foreground hover:text-foreground"
                    onClick={triggerDismiss}
                >
                    <X className="size-3.5" />
                </Button>
            </div>
        </div>
    );
}

function Notifications() {
    const { data: notifications = [], isLoading } = useNotifications();
    const { data: inventoryData } = useInventoryAll();
    const markRead = useMarkNotificationRead();
    const markAllRead = useMarkAllNotificationsRead();
    const dismiss = useDismissNotification();
    const syncedRef = useRef(false);

    useEffect(() => {
        if (!inventoryData?.list || syncedRef.current) return;
        syncedRef.current = true;
        syncInventoryNotifications(inventoryData.list).catch(console.error);
    }, [inventoryData]);

    const unreadCount = notifications.filter((n) => !n.read_at).length;

    return (
        <Layout>
            <div className="mx-auto max-w-2xl px-4 py-8">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-semibold">Notifications</h1>
                        {unreadCount > 0 && (
                            <p className="text-sm text-muted-foreground">
                                {unreadCount} unread
                            </p>
                        )}
                    </div>
                    {unreadCount > 0 && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => markAllRead.mutate()}
                            disabled={markAllRead.isPending}
                        >
                            Mark all as read
                        </Button>
                    )}
                </div>

                {isLoading ? (
                    <div className="space-y-3">
                        {[1, 2, 3].map((i) => (
                            <div
                                key={i}
                                className="h-20 rounded-lg bg-muted animate-pulse"
                            />
                        ))}
                    </div>
                ) : notifications.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-24 text-muted-foreground gap-3">
                        <Bell className="size-10 opacity-30" />
                        <p className="text-sm">You're all caught up</p>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {notifications.map((notification) => (
                            <SwipeableNotification
                                key={notification.id}
                                notification={notification}
                                onDismiss={() =>
                                    dismiss.mutate(notification.id)
                                }
                                onMarkRead={() =>
                                    markRead.mutate(notification.id)
                                }
                            />
                        ))}
                    </div>
                )}
            </div>
        </Layout>
    );
}